import Vue from 'vue'
import Router from 'vue-router'
import Auth from '@okta/okta-vue'
import OktaConfig from './oktaConfig'

Vue.use(Auth, OktaConfig.oidc)

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      name: 'default',
      path: '/',
      meta: {
        title: 'Loading Application',
      },
      component: () => import('./components/DynamicComponents/LandingComponent'),
    },

    // Okta Page
    {
      name: 'callback',
      path: '/callback',
      component: Auth.handleCallback(),
    },

    // Unauthorized User
    {
      name: 'unauthorized',
      path: '/unauthorized',
      meta: {
        title: 'Unauthorized User',
      },
      component: () => import('./components/DynamicComponents/Unauthorized'),
    },

    // Main Components
    {
      name: 'layout',
      path: '/layout',
      redirect: 'dashboard',
      component: () => import('./layouts/Main'),
      meta: {
        requiresAuth: true,
      },
      children: [{
        name: 'dashboard',
        path: '/dashboard',
        meta: {
          title: 'Dashboard',
        },
        component: () => import('./components/DynamicComponents/Projects/Dashboard'),
      },
      {
        name: 'project',
        path: '/dashboard/project/:id',
        meta: {
          title: 'Project',
        },
        component: () => import('./components/DynamicComponents/Templates/Navigations/Main'),
      },
      {
        name: 'reports',
        path: '/dashboard/project/:id/reports',
        meta: {
          title: 'Reports',
        },
        component: () => import('./components/DynamicComponents/Projects/Reports'),
      },
      {
        name: 'restoreproject',
        path: '/restoreproject',
        meta: {
          title: 'Restore Project',
        },
        component: () => import('./components/DynamicComponents/Projects/Restore'),
      },

      // Admin Pages
      {
        name: 'users',
        path: '/users',
        meta: {
          title: 'Users',
        },
        component: () => import('./components/DynamicComponents/Admin/Users'),
      },
      {
        path: '/templates',
        name: 'templates',
        meta: {
          title: 'Templates',
        },
        component: () => import('./components/DynamicComponents/Templates/List'),
      },
      {
        path: '/templates/:id',
        name: 'templatesid',
        meta: {
          title: 'Template Details',
        },
        component: () => import('./components/DynamicComponents/Templates/Navigations/Main'),
      },
      {
        name: 'setting',
        path: '/setting',
        meta: {
          title: 'setting',
        },
        component: () => import('./components/DynamicComponents/Admin/Settings'),
      },

      // Master Pages
      {
        path: '/master',
        component: () => import('./components/DynamicComponents/Admin/MasterData'),
        children: [
          {
            name: 'master',
            path: '',
            meta: {
              type: 'analysis-type',
            },
          },
          {
            name: 'analysisType',
            path: 'analysis-type',
            meta: {
              title: 'Analysis Type',
              type: 'analysis-type',
            },
          },
          {
            name: 'structureProfile',
            path: 'structure-profile',
            meta: {
              title: 'Structure Profile',
              type: 'structure-profile',
            },
          },
          {
            name: 'buildingUse',
            path: 'building-use',
            meta: {
              title: 'Building Use',
              type: 'building-use',
            },
          },
          {
            name: 'unit',
            path: 'unit',
            meta: {
              title: 'Unit',
              type: 'unit',
            },
          },
          {
            name: 'constructionQualityFactor',
            path: 'construction-quality-factor',
            meta: {
              title: 'Construction Quality Factor',
              type: 'construction-quality-factor',
            },
          },
          {
            name: 'conditionFactor',
            path: 'condition-factor',
            meta: {
              title: 'Condition Factor',
              type: 'condition-factor',
            },
          },
          {
            name: 'assetClass',
            path: 'asset-class',
            meta: {
              title: 'Asset Class',
              type: 'asset-class',
            },
          },
          {
            name: 'categoryCode',
            path: 'category-code',
            meta: {
              title: 'Category Code',
              type: 'category-code',
            },
          },
          {
            name: 'takeoff',
            path: 'takeoff',
            meta: {
              title: 'Takeoff',
              type: 'takeoff',
            },
          },
          {
            name: 'groupingBank',
            path: 'grouping-bank',
            meta: {
              title: 'Grouping Bank',
              type: 'grouping-bank',
            },
          },
          {
            name: 'tenantBank',
            path: 'tenant-bank',
            meta: {
              title: 'Tenant Bank',
              type: 'tenant-bank',
            },
          },
          {
            name: 'bsuop',
            path: 'bsuop',
            meta: {
              title: 'BSUOP',
              type: 'bsuop',
            },
          },
          {
            name: 'historicalCostIndex',
            path: 'historical-cost-index',
            meta: {
              title: 'Historical Cost Index',
              type: 'historical-cost-index',
            },
          },
          {
            name: 'contract',
            path: 'contract',
            meta: {
              title: 'Contract',
              type: 'contract',
            },
          },
          {
            name: 'locationFactor',
            path: 'location-factor',
            meta: {
              title: 'Location Factor',
              type: 'location-factor',
            },
          },
          {
            name: 'tenantType',
            path: 'tenant-type',
            meta: {
              title: 'Tenant Type',
              type: 'tenant-type',
            },
          },
          {
            name: 'depreciationSchedule',
            path: 'depreciation-schedule',
            meta: {
              title: 'Depreciation Schedule',
              type: 'depreciation-schedule',
            },
          },
        ],
      },

      // Grouping Bank Details
      {
        name: 'groupingBankDetails',
        path: '/master/grouping-bank/:id',
        meta: {
          title: 'Grouping Bank Details',
          type: 'groupingBank',
        },
        component: () => import('./components/DynamicComponents/Admin/MasterData/GroupingBank/BankDetails'),
      },

      // Tenant Bank Details
      {
        name: 'tenantBankDetails',
        path: '/master/tenant-bank/:id',
        meta: {
          title: 'Tenant Bank Details',
          type: 'tenantBank',
        },
        component: () => import('./components/DynamicComponents/Admin/MasterData/TenantBank/BankDetails'),
      },

      // User
      {
        name: 'masteprofiler',
        path: '/profile',
        meta: {
          title: 'Profile',
        },
        component: () => import('./components/DynamicComponents/User/profile'),
      },

      // Release Notes
      {
        name: 'releaseNotes',
        path: '/releaseNotes',
        meta: {
          title: 'Release Notes',
        },
        component: () => import('./components/DynamicComponents/ReleaseNotes'),
      },

      // 404
      {
        name: '404',
        path: '/404',
        meta: {
          title: '404',
        },
        component: () => import('./components/StaticComponent/404'),
      },
      ],
    },

    // Redirect to 404
    {
      path: '*',
      redirect: '/404',
      hidden: true,
    },
  ],
})

router.beforeEach((to, from, next) => {
  if (from.path === '/' && to.path !== '/' && !(to.path.indexOf('callback') >= 0 && to.fullPath.indexOf('id_token') >= 0)) {
    if (!to.params.loaded && to.name !== 'unauthorized') {
      next({
        name: 'default',
        params: {
          route: to,
        },
      })
    }
  }

  Vue.prototype.$auth.authRedirectGuard()
  next()
})

export default router
